/**
 * Carbon table filter: a 48px funnel button in the toolbar that opens a
 * layer-02 menu (headless UI Popover, `data-menu`, the menu shadow) of
 * checkbox groups and an optional date range (with quick-range tags when
 * `presets` is set). Choices apply as they change;
 * the footer resets them or closes the menu. The trigger names how many
 * filters are on ("Filter drifts, 2 active").
 */
import type { ReactNode } from 'react'
import { Popover } from '@headlessui/react'
import clsx from 'clsx'
import { Button } from './Button'
import { Checkbox } from './Checkbox'
import { DateInput } from './DateInput'
import { FIELD_ON_LAYER_02 } from './fieldStyles'
import { IconFilter } from './icons'
import { Tag } from './Tag'

export interface FilterOption {
  value: string
  label: ReactNode
}

export interface FilterGroup {
  legend: string
  options: FilterOption[]
  selected: string[]
  onChange: (selected: string[]) => void
}

export interface FilterDateRange {
  /** ISO dates (`yyyy-mm-dd`); empty for an open end. */
  from: string
  to: string
  onChange: (range: { from: string; to: string }) => void
  legend?: string
  /**
   * Quick ranges above the From/To fields: All time (both ends open), 7 days
   * and 30 days (ending today, local dates). The one matching the range is pressed.
   */
  presets?: boolean
}

const DAY_PRESETS = [
  { id: 'all', label: 'All time', days: null },
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
] as const

/** Local calendar date as `yyyy-mm-dd`, the value a date input holds. */
function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** The range a preset stands for, ending today. */
function presetRange(days: number | null, today = new Date()): { from: string; to: string } {
  if (days === null) return { from: '', to: '' }
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days)
  return { from: isoDay(from), to: isoDay(today) }
}

export interface TableFilterMenuProps {
  /** The trigger's accessible name ("Filter drifts"). */
  label: string
  groups: FilterGroup[]
  dateRange?: FilterDateRange
  onReset: () => void
  className?: string
}

function DatePresets({ range }: { range: FilterDateRange }) {
  return (
    <div role="group" aria-label="Quick ranges" className="mb-4 flex flex-wrap gap-2">
      {DAY_PRESETS.map((preset) => {
        const r = presetRange(preset.days)
        return (
          <Tag key={preset.id} selected={range.from === r.from && range.to === r.to} onClick={() => range.onChange(r)}>
            {preset.label}
          </Tag>
        )
      })}
    </div>
  )
}

export function TableFilterMenu({ label, groups, dateRange, onReset, className }: TableFilterMenuProps) {
  const active = groups.reduce((n, g) => n + g.selected.length, 0) + (dateRange && (dateRange.from || dateRange.to) ? 1 : 0)
  return (
    <Popover className={clsx('relative flex flex-none', className)}>
      <Popover.Button
        aria-label={active ? `${label}, ${active} active` : label}
        className={clsx(
          'inline-flex h-12 min-w-12 flex-none items-center justify-center gap-2 px-4 text-text-secondary transition-colors duration-110 ease-carbon',
          'hover:bg-layer-hover hover:text-text-primary aria-expanded:bg-layer-2 aria-expanded:text-text-primary',
        )}
      >
        <IconFilter />
        {active ? <span aria-hidden="true" className="text-helper tabular-nums text-text-primary">{active}</span> : null}
      </Popover.Button>
      <Popover.Panel
        data-menu=""
        className={clsx(
          'absolute right-0 top-full z-30 flex max-h-[min(32rem,70vh)] w-[min(320px,calc(100vw-32px))] flex-col bg-layer-2 text-text-primary shadow-menu',
          FIELD_ON_LAYER_02,
        )}
      >
        {({ close }) => (
          <>
            <div className="flex min-h-0 flex-col gap-6 overflow-y-auto p-4">
              {groups.map((group) => (
                <fieldset key={group.legend} className="min-w-0">
                  <legend className="mb-3 text-helper text-text-secondary">{group.legend}</legend>
                  <div className="flex flex-col gap-3">
                    {group.options.map((option) => {
                      const on = group.selected.includes(option.value)
                      return (
                        <Checkbox
                          key={option.value}
                          label={option.label}
                          checked={on}
                          onChange={() => group.onChange(on ? group.selected.filter((v) => v !== option.value) : [...group.selected, option.value])}
                        />
                      )
                    })}
                  </div>
                </fieldset>
              ))}
              {dateRange ? (
                <fieldset className="min-w-0">
                  <legend className="mb-3 text-helper text-text-secondary">{dateRange.legend ?? 'Date range'}</legend>
                  {dateRange.presets ? <DatePresets range={dateRange} /> : null}
                  <div className="grid grid-cols-2 gap-4">
                    <DateInput
                      label="From"
                      value={dateRange.from}
                      max={dateRange.to || undefined}
                      onChange={(e) => dateRange.onChange({ from: e.target.value, to: dateRange.to })}
                    />
                    <DateInput
                      label="To"
                      value={dateRange.to}
                      min={dateRange.from || undefined}
                      onChange={(e) => dateRange.onChange({ from: dateRange.from, to: e.target.value })}
                    />
                  </div>
                </fieldset>
              ) : null}
            </div>
            <div className="grid flex-none grid-cols-2">
              <Button kind="secondary" fullWidth onClick={onReset} disabled={!active}>
                Reset filters
              </Button>
              <Button kind="primary" fullWidth onClick={() => close()}>
                Done
              </Button>
            </div>
          </>
        )}
      </Popover.Panel>
    </Popover>
  )
}

export default TableFilterMenu
